import { useEffect, useState } from 'react';

import {
  useOutreachSettings,
  useUpdateOutreachSettings,
} from '../../hooks/useOutreach';
import { formatEt } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';


/**
 * Outreach Settings sub-tab. Singleton config row -- currently just the
 * global test-mode toggle and the test-recipient address.
 *
 * Test mode here is the BLANKET switch: when on, every campaign send
 * (including via Start sending and via Send-test buttons) routes to the
 * test recipient with a [TEST] subject prefix and a body banner. Per-
 * campaign test mode ORs with this; neither overrides the other.
 */
export function OutreachSettingsAdmin() {
  const settingsQ = useOutreachSettings();
  const updateMut = useUpdateOutreachSettings();

  const [testRecipient, setTestRecipient] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Hydrate the editable fields once the query lands.
  useEffect(() => {
    if (settingsQ.data) {
      setTestRecipient(settingsQ.data.test_recipient);
    }
  }, [settingsQ.data?.test_recipient]);

  if (settingsQ.isLoading) {
    return <div className={styles.empty}>Loading…</div>;
  }
  if (!settingsQ.data) {
    return <div className={styles.empty}>Settings unavailable.</div>;
  }
  const s = settingsQ.data;

  async function toggleGlobalTestMode() {
    setError(null);
    try {
      await updateMut.mutateAsync({ test_mode_enabled: !s.test_mode_enabled });
      setSavedAt(Date.now());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Toggle failed.');
    }
  }

  async function saveTestRecipient() {
    setError(null);
    const recip = testRecipient.trim();
    if (!recip || !recip.includes('@')) {
      setError('Test recipient must be a valid email.');
      return;
    }
    try {
      await updateMut.mutateAsync({ test_recipient: recip });
      setSavedAt(Date.now());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  const dirty = testRecipient.trim() !== s.test_recipient;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Outreach settings</h2>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div
        style={{
          padding: 16, background: '#FAF9F4', borderRadius: 6,
          border: s.test_mode_enabled ? '2px solid #B04030' : '1px solid #E5E5E0',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: "'DM Sans', sans-serif", fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={s.test_mode_enabled}
              onChange={toggleGlobalTestMode}
              disabled={updateMut.isPending}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <strong>Global test mode</strong>
          </label>
          {s.test_mode_enabled && (
            <span
              style={{
                fontSize: 11, fontFamily: "'DM Mono', monospace",
                color: '#B04030', background: '#FCEFEC',
                padding: '2px 8px', borderRadius: 10,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}
            >
              ON
            </span>
          )}
        </div>
        <div className={styles.helpText} style={{ marginTop: 8 }}>
          When on, every outbound email from any campaign routes to the
          test recipient with a <code>[TEST]</code> subject prefix and a
          body banner listing the original recipients. Status write-back
          is skipped, click tracking is skipped, and unsubscribe / RSVP
          links are inert. ORs with each campaign's own test toggle —
          neither overrides the other.
        </div>
      </div>

      <div className={styles.formGrid}>
        <label className={styles.label}>Test recipient</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className={styles.input}
            type="email"
            placeholder="brad+test@ember-infra.com"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={saveTestRecipient}
            disabled={!dirty || updateMut.isPending}
          >
            {updateMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <div className={styles.helpText} style={{ marginTop: 4 }}>
        Address that receives all routed test sends. Plus-addressing
        (e.g., <code>brad+test@</code>) is supported by Microsoft 365 and
        routes back to the base mailbox.
      </div>

      <div className={styles.helpText} style={{ marginTop: 24, fontSize: 11 }}>
        Last updated by {s.updated_by || 'unknown'} on{' '}
        {formatEt(s.updated_at)}
        {savedAt && ` · saved ${formatEt(new Date(savedAt).toISOString())}`}
      </div>
    </div>
  );
}


/**
 * Persistent banner across the Outreach tab when test mode is on. Mounted
 * once at the OutreachPage root so it follows the user across sub-tabs.
 */
export function TestModeBanner() {
  const settingsQ = useOutreachSettings();
  if (!settingsQ.data?.test_mode_enabled) return null;
  return (
    <div
      style={{
        background: '#FCEFEC', border: '1px solid #E5B5B0',
        color: '#7B2820', padding: '8px 12px', borderRadius: 6,
        fontFamily: "'DM Sans', sans-serif", fontSize: 13,
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
      }}
    >
      <span style={{ fontSize: 16 }}>⚠</span>
      <span>
        <strong>Global test mode is ON.</strong>{' '}
        Outbound emails route to{' '}
        <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>
          {settingsQ.data.test_recipient}
        </code>
        . No status write-back, no click tracking, links are inert.
      </span>
    </div>
  );
}
